import Button from 'Components/Base/Button'
import Row from 'Components/Base/Row'
import { FormikErrors, getIn, useFormik } from 'formik'
import { useAppSelector } from 'hooks/hooks'
import usePatientIntake from 'hooks/usePatientIntake'
import React, { useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { getAccessToken } from 'reducers/authSlice'
import styled, { useTheme } from 'styled-components'
import * as Yup from 'yup'
import Progress from '../components/Progress'

interface QuestionAnswer {
  questionId: number
  answerId: number
  programId: number
}
export interface LifestyleFormValues {
  enrollmentFormGuid?: string
  heightFeet: number | undefined
  heightInches: number | undefined
  weight: number | undefined
  responses: QuestionAnswer[]
  quit?: boolean
}

export interface LifestyleTempFormValues {
  heightFeet: number | undefined
  heightInches: number | undefined
  weight: number | undefined
  tobaccoQuestionAnswers: QuestionAnswer
  caffeineQuestionAnswers: QuestionAnswer
  alcoholQuestionAnswers: QuestionAnswer
  exerciseQuestionAnswers: QuestionAnswer
}

interface props {
  handleSubmit: Function
  backPressed: Function
}

export default function PALifestyle({ handleSubmit, backPressed }: props) {
  const theme = useTheme()
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const accessToken = useAppSelector(getAccessToken)
  const { getLifeStyleInfo, postLifestyleInfo } = usePatientIntake({ token: accessToken })

  useEffect(() => {
    getLifeStyleInfo().then((res) => {
      formik.setFieldValue('heightFeet', res.data.heightFt)
      formik.setFieldValue('heightInches', res.data.heightIn)
      formik.setFieldValue('weight', res.data.weight)
      if (res.data.responses.length > 0) {
        formik.setFieldValue(
          'tobaccoQuestionAnswers',
          res.data.responses.find((x) => x.questionId === 12) ?? { questionId: 12, answerId: 18, programId: 3 },
        )
      }
      if (res.data.responses.length > 0 && res.data.responses.some((x) => x.questionId === 30)) {
        formik.setFieldValue(
          'caffeineQuestionAnswers',
          res.data.responses.find((x) => x.questionId === 30) ?? { questionId: 30, answerId: 18, programId: 3 },
        )
      }
      if (res.data.responses.length > 0 && res.data.responses.some((x) => x.questionId === 31)) {
        formik.setFieldValue(
          'alcoholQuestionAnswers',
          res.data.responses.find((x) => x.questionId === 31) ?? { questionId: 31, answerId: 18, programId: 3 },
        )
      }
      if (res.data.responses.length > 0 && res.data.responses.some((x) => x.questionId === 32)) {
        formik.setFieldValue(
          'exerciseQuestionAnswers',
          res.data.responses.find((x) => x.questionId === 32) ?? { questionId: 32, answerId: 18, programId: 3 },
        )
      }
    })
  }, [])

  const initialValues: any = {
    heightFeet: '',
    heightInches: '',
    weight: '',
    tobaccoQuestionAnswers: { questionId: 12, answerId: 18, programId: 3 },
    caffeineQuestionAnswers: { questionId: 30, answerId: 18, programId: 4 },
    alcoholQuestionAnswers: { questionId: 31, answerId: 18, programId: 4 },
    exerciseQuestionAnswers: { questionId: 32, answerId: 18, programId: 4 },
  }
  const onSubmit = () => {
    const formAnswers: LifestyleFormValues = {
      enrollmentFormGuid: undefined,
      heightFeet: formik.values.heightFeet,
      heightInches: formik.values.heightInches,
      weight: formik.values.weight,
      responses: [],
    }

    formAnswers.responses.push(formik.values.tobaccoQuestionAnswers)
    formAnswers.responses.push(formik.values.caffeineQuestionAnswers)
    formAnswers.responses.push(formik.values.alcoholQuestionAnswers)
    formAnswers.responses.push(formik.values.exerciseQuestionAnswers)

    postLifestyleInfo(formAnswers)
    handleSubmit()
  }

  const handleBack = () => {
    const formAnswers: LifestyleFormValues = {
      enrollmentFormGuid: undefined,
      heightFeet: formik.values.heightFeet,
      heightInches: formik.values.heightInches,
      weight: formik.values.weight,
      responses: [],
    }

    formAnswers.responses.push(formik.values.tobaccoQuestionAnswers)
    formAnswers.responses.push(formik.values.caffeineQuestionAnswers)
    formAnswers.responses.push(formik.values.alcoholQuestionAnswers)
    formAnswers.responses.push(formik.values.exerciseQuestionAnswers)

    postLifestyleInfo(formAnswers)
    backPressed()
  }

  const setMobileSetting = () => {
    if (window.innerWidth < 800) {
      setIsMobile(true)
    } else {
      setIsMobile(false)
    }
  }

  useEffect(() => {
    setMobileSetting()
    window.addEventListener('resize', () => {
      setMobileSetting()
    })
  }, [])

  const validationSchema = Yup.object().shape({
    heightFeet: Yup.number().required('Height (ft) is required').min(2, 'Height must be greater than 3 feet'),
    heightInches: Yup.number().required('Height (in) is required'),
    weight: Yup.number().required('Weight is required').min(79, 'Weight must be greater than 80lbs'),
  })

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })
  function getStyles(fieldName: string, errors: FormikErrors<any>) {
    if (getIn(errors, fieldName)) {
      return {
        border: '2px solid red',
        boxShadow: 'none',
      }
    }
  }

  return (
    <Container>
      <Card>
        <Progress current={3} total={3} />
        <Header>Your Lifestyle & Habits</Header>
        <Para>Just a few quick questions about your daily habits. This helps your pharmacist get to know you better.</Para>
        <Para>
          <b>What is your height, weight, and sex?</b>
        </Para>
        <Form>
          <StyledFormSet>
            <Row styles={isMobile ? {} : { marginRight: 'auto' }}>
              <StyledFormInput
                type="number"
                onChange={(value) => formik.setFieldValue('heightFeet', value.currentTarget.value)}
                style={{
                  border: '2px solid transparent',
                  borderRadius: 4,

                  ...getStyles('heightFeet', formik.errors),
                }}
                value={formik.values.heightFeet}
                placeholder="Height (ft)"
              />
            </Row>
            <Row>
              <StyledFormInput
                type="number"
                onChange={(value) => formik.setFieldValue('heightInches', value.currentTarget.value)}
                style={{
                  border: '2px solid transparent',
                  borderRadius: 4,
                  ...getStyles('heightInches', formik.errors),
                }}
                value={formik.values.heightInches}
                placeholder="Height (in)"
              />
            </Row>
          </StyledFormSet>
          <StyledFormSet>
            <Row>
              <StyledWidthInput
                type="number"
                onChange={(value) => formik.setFieldValue('weight', value.currentTarget.value)}
                style={{
                  border: '2px solid transparent',
                  borderRadius: 4,
                  ...getStyles('weight', formik.errors),
                }}
                value={formik.values.weight}
                placeholder="Weight (lbs)"
              />
            </Row>
          </StyledFormSet>
        </Form>
        <Para>
          <b>Have you ever used tobacco, or do you use it now?</b>
        </Para>
        <p>(This includes things like cigarettes, cigars, chewing tobacco, or vaping.)</p>
        {/* 17 is yes 18 is no */}
        <Switch>
          <Checkbox
            type="checkbox"
            checked={formik.values.tobaccoQuestionAnswers.answerId === 17}
            onChange={() =>
              formik.setFieldValue(
                'tobaccoQuestionAnswers',
                formik.values.tobaccoQuestionAnswers.answerId === 18
                  ? { questionId: 12, answerId: 17, programId: 3 }
                  : { questionId: 12, answerId: 18, programId: 3 },
              )
            }
          />
          <Slider />
        </Switch>
        <Para>
          <b>Do you have caffeine regularly?</b>
        </Para>
        <Switch>
          <Checkbox
            type="checkbox"
            checked={formik.values.caffeineQuestionAnswers.answerId === 17}
            onChange={() =>
              formik.setFieldValue(
                'caffeineQuestionAnswers',
                formik.values.caffeineQuestionAnswers.answerId === 18
                  ? { questionId: 30, answerId: 17, programId: 4 }
                  : { questionId: 30, answerId: 18, programId: 4 },
              )
            }
          />
          <Slider />
        </Switch>
        <Para>
          <b>Do you drink alcohol?</b>
        </Para>
        <Switch>
          <Checkbox
            type="checkbox"
            checked={formik.values.alcoholQuestionAnswers.answerId === 17}
            onChange={() =>
              formik.setFieldValue(
                'alcoholQuestionAnswers',
                formik.values.alcoholQuestionAnswers.answerId === 18
                  ? { questionId: 31, answerId: 17, programId: 4 }
                  : { questionId: 31, answerId: 18, programId: 4 },
              )
            }
          />
          <Slider />
        </Switch>
        <Para>
          <b>Do you exercise?</b>
        </Para>
        <Switch>
          <Checkbox
            type="checkbox"
            checked={formik.values.exerciseQuestionAnswers.answerId === 17}
            onChange={() =>
              formik.setFieldValue(
                'exerciseQuestionAnswers',
                formik.values.exerciseQuestionAnswers.answerId === 18
                  ? { questionId: 32, answerId: 17, programId: 4 }
                  : { questionId: 32, answerId: 18, programId: 4 },
              )
            }
          />
          <Slider />
        </Switch>
        {Object.keys(formik.errors).length !== 0 && <ErrorText>Please fill out all required fields.</ErrorText>}
        <Row styles={{ width: '100%', justifyContent: 'space-between', marginTop: 20 }}>
          <Button
            text="Back"
            styles={{ marginBottom: 30, background: 'none', color: theme.colors.primary1, border: `1px solid ${theme.colors.primary1}` }}
            width={100}
            onClick={() => handleBack()}
          />
          <Button text="Submit" styles={{ marginBottom: 30, backgroundColor: theme.colors.status.good }} width={150} onClick={formik.handleSubmit} />
        </Row>
      </Card>
      <Image src="https://triacentral.blob.core.windows.net/dev-storage/portal/enrollment/Quote%201.png" />
    </Container>
  )
}

const Container = styled.div`
  font-family: Raleway;
  display: flex;
  max-width: 1200px;

  margin-left: auto;
  margin-right: auto;
  height: fit-content;
  justify-content: space-between;
  @media only screen and (max-width: 600px) {
    margin-top: 20px;
  }
`

const ErrorText = styled.p`
  font-size: 18px;
  margin-top: 10px;
  color: ${({ theme }) => `${theme.colors.status.bad}`};
`

const Card = styled.div`
  font-family: Raleway;
  max-width: 600px;
  min-width: 600px;
  height: fit-content;
  min-height: 640px;
  padding: 40px;
  text-align: left;
  @media only screen and (max-width: 600px) {
    padding: 0px 20px 40px;
    margin-top: 0px;
    min-height: fit-content;
    max-width: 100%;
    min-width: 100%;
  }
`

const Header = styled.h1`
  font-weight: 700;
  font-size: 36px;
  margin-bottom: 20px;
`

const Para = styled.p`
  font-size: 18px;
  margin-bottom: 10px;
`

const StyledFormSet = styled.div`
  flex-direction: row;
  display: flex;
  min-width: 100%;
  @media (max-width: 600px) {
    flex-direction: column;
  }
`
const StyledFormInput = styled(Form.Control)`
  background-color: #edfdff;
  text-align: center;
  border-radius: 3px;
  box-shadow: inset 0px 1px 3px;
  min-height: 50px;
  margin-bottom: 10px;
  width: 200px;
  @media (max-width: 600px) {
    width: 100%;
  }
`

const StyledWidthInput = styled(Form.Control)`
  background-color: #edfdff;
  text-align: center;
  border-radius: 3px;
  box-shadow: inset 0px 1px 3px;
  min-height: 50px;
  margin-bottom: 20px;
  width: 200px;
  @media (max-width: 600px) {
    width: 100%;
  }
`

const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 60px;
  height: 24px; /* thinner height */
  margin-bottom: 20px;
`

const Checkbox = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: ${({ theme }) => `${theme.colors.status.good}`};
  }

  &:checked + span:before {
    transform: translateX(36px); /* adjust based on knob size */
  }
`

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: '';
    height: 16px;
    width: 16px; /* smaller knob */
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`

const Image = styled.img`
  max-height: 530px;
  padding: 40px;
  @media only screen and (max-width: 1200px) {
    display: none;
  }
`
